import {
  Building2Icon,
  ChartColumnIcon,
  DatabaseBackupIcon,
  FileChartColumnIcon,
  FolderKanbanIcon,
  ChevronsUpDownIcon,
  LogOutIcon,
  MonitorSmartphoneIcon,
  MoonIcon,
  SunIcon,
  SunMoonIcon,
  PanelLeftIcon,
  UsersIcon
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { NavLink, useMatch } from 'react-router-dom'
import appIcon from '@/assets/icon.png'
import { ProjectSheetIcon } from '@/components/icons/project-sheet-icon'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useSession } from '@/hooks/use-session'
import { authClient } from '@/lib/auth-client'
import { initials } from '@/lib/initials'
import { setTheme, useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

type NavigationItemProps = {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

function NavigationItem({ to, label, icon: Icon }: NavigationItemProps): React.JSX.Element {
  const isActive = useMatch({ path: to, end: false }) !== null

  return (
    <SidebarMenuItem>
      <SidebarMenuButton render={<NavLink to={to} />} isActive={isActive} tooltip={label}>
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * App name with the collapse toggle beside it. In the collapsed rail the app icon itself is the
 * toggle and turns into the panel icon on hover.
 */
function SidebarBrand(): React.JSX.Element {
  const { state, isMobile, toggleSidebar } = useSidebar()

  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            tooltip="Expand sidebar"
            aria-label="Expand sidebar"
            onClick={toggleSidebar}
          >
            <img
              src={appIcon}
              alt=""
              className="size-8 shrink-0 group-hover/menu-button:hidden group-focus-visible/menu-button:hidden"
            />
            <span className="hidden size-8 items-center justify-center group-hover/menu-button:flex group-focus-visible/menu-button:flex">
              <PanelLeftIcon />
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <SidebarMenu className="min-w-0 flex-1">
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" render={<NavLink to="/project-sheet" />}>
            <img src={appIcon} alt="" className="size-8 shrink-0" />
            <span className="text-base font-bold tracking-wide uppercase">
              <span className="text-primary">C-</span>Sheet
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarTrigger
        aria-label={isMobile ? 'Close navigation' : 'Collapse sidebar'}
        title={isMobile ? 'Close navigation' : 'Collapse sidebar'}
        className="text-muted-foreground"
      />
    </div>
  )
}

/** Sun and moon pill showing the current theme; the surrounding button toggles it. */
function ThemeSwitch({ dark }: { dark: boolean }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="relative ml-auto h-6 w-12 shrink-0 rounded-full bg-foreground/10"
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 size-5 rounded-full bg-background shadow-sm transition-transform',
          dark && 'translate-x-6'
        )}
      />
      <SunIcon
        className={cn('absolute top-1 left-1', dark ? 'text-muted-foreground' : 'text-amber-500')}
      />
      <MoonIcon
        className={cn('absolute top-1 left-7', dark ? 'text-primary' : 'text-muted-foreground')}
      />
    </span>
  )
}

export function AppSidebar(): React.JSX.Element {
  const session = useSession()
  const queryClient = useQueryClient()
  const user = session.data
  const dark = useTheme() === 'dark'
  const { isMobile } = useSidebar()

  async function signOut(): Promise<void> {
    await authClient.signOut()
    await queryClient.resetQueries()
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarBrand />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavigationItem to="/project-sheet" label="Project sheet" icon={ProjectSheetIcon} />
              <NavigationItem to="/budgets" label="Budgets" icon={ChartColumnIcon} />
              <NavigationItem to="/reports" label="Reports" icon={FileChartColumnIcon} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Manage</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavigationItem to="/projects" label="Projects" icon={FolderKanbanIcon} />
              <NavigationItem to="/people" label="People" icon={UsersIcon} />
              <NavigationItem to="/clients" label="Clients" icon={Building2Icon} />
              <NavigationItem to="/data" label="Data & backup" icon={DatabaseBackupIcon} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              role="switch"
              aria-checked={dark}
              aria-label="Dark theme"
              tooltip="Theme"
              onClick={() => setTheme(dark ? 'light' : 'dark')}
            >
              <SunMoonIcon />
              <span>Theme</span>
              <ThemeSwitch dark={dark} />
            </SidebarMenuButton>
          </SidebarMenuItem>
          {user && (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      size="lg"
                      aria-label={`Account menu for ${user.name}`}
                      className="data-popup-open:bg-sidebar-accent"
                    />
                  }
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                    {initials(user.name) || '?'}
                  </span>
                  <span className="grid min-w-0 flex-1 text-left leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                  </span>
                  <ChevronsUpDownIcon className="ml-auto text-muted-foreground" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={isMobile ? 'top' : 'right'}
                  align="end"
                  className="min-w-56"
                >
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="flex flex-col">
                      <span className="truncate font-medium text-foreground">{user.name}</span>
                      <span className="truncate font-normal">{user.email}</span>
                    </DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem render={<NavLink to="/sessions" />}>
                      <MonitorSmartphoneIcon />
                      Sessions
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void signOut()}>
                      <LogOutIcon />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
